import * as XLSX from "xlsx";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const n = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const normalizeKey = (key) =>
  String(key || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const cleanRow = (row) => {
  const out = {};
  Object.entries(row || {}).forEach(([key, value]) => {
    out[normalizeKey(key)] = value;
  });
  return out;
};

export async function readDailyRideHailingFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  return XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
}

export function detectDailyPlatform(rows = []) {
  if (!rows.length) return "";
  const first = cleanRow(rows[0]);

  if (
    Object.prototype.hasOwnProperty.call(first, "driver_uuid") &&
    Object.prototype.hasOwnProperty.call(first, "total_earnings") &&
    Object.prototype.hasOwnProperty.call(first, "payouts_cash_collected")
  ) {
    return "uber";
  }

  if (
    Object.prototype.hasOwnProperty.call(first, "driver_identifier") &&
    Object.prototype.hasOwnProperty.call(first, "collected_cash_aed") &&
    Object.prototype.hasOwnProperty.call(first, "projected_payout_aed")
  ) {
    return "bolt";
  }

  return "";
}

export function normalizeDailyRows(rows = [], platform = "") {
  const p = String(platform || "").toLowerCase();

  if (!['uber', 'bolt'].includes(p)) {
    throw new Error("Daily upload currently supports Uber and Bolt only.");
  }

  return rows
    .map((raw) => {
      const r = cleanRow(raw);

      if (p === "uber") {
        const driverId = String(r.driver_uuid || "").trim();
        const firstName = String(r.driver_first_name || "").trim();
        const lastName = String(r.driver_surname || r.driver_last_name || "").trim();
        const driverName = `${firstName} ${lastName}`.replace(/\s+/g, " ").trim();

        // Uber driver exports can also contain a COMPANY settlement row. It is not
        // a driver earning and must never be shown as an unmapped driver.
        //
        // IMPORTANT: only known company/bank-transfer rows are skipped. Every other
        // unfamiliar Uber driver is kept so a NEW DRIVER still appears in the mapping
        // section and can be linked to the saved Drivers database.
        const normalizedName = driverName.toLowerCase().replace(/\s+/g, " ").trim();
        const transferredToBank = toNumber(r.payouts_transferred_to_bank_account);
        const isKnownCompanyName =
          normalizedName.includes("comfort line") ||
          normalizedName.includes("lolia limousine");
        const isCompanyBankTransfer = Math.abs(transferredToBank) > 0;

        if (isKnownCompanyName || isCompanyBankTransfer) return null;

        // Validated against the user's weekly Uber settlement:
        // Bank portion = Total earnings + Refunds & Expenses + Payouts.
        // Cash is normally reported as a negative payout. Keep the sign because
        // Uber can also post positive cash adjustments/credits. Negating the
        // signed value makes normal cash positive and a positive adjustment
        // correctly reduce the driver's net cash for the day.
        const totalEarnings = toNumber(r.total_earnings);
        const refundsExpenses = toNumber(r.refunds_expenses);
        const payouts = toNumber(r.payouts);
        const cashCollected = -toNumber(r.payouts_cash_collected);

        return {
          raw,
          platform: p,
          driver_id: driverId,
          name: driverName,
          bank_income: totalEarnings + refundsExpenses + payouts,
          cash_income: cashCollected,
        };
      }

      const driverId = String(r.driver_identifier || r.individual_identifier || "").trim();
      const driverName = String(r.driver || "").replace(/\s+/g, " ").trim();

      // Validated against the user's Bolt weekly report:
      // Projected payout = bank; Collected cash = driver cash.
      return {
        raw,
        platform: p,
        driver_id: driverId,
        name: driverName,
        bank_income: toNumber(r.projected_payout_aed),
        cash_income: toNumber(r.collected_cash_aed),
      };
    })
    .filter((row) => row && (row.driver_id || row.name));
}

function isoFromParts(year, month, day) {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!y || !m || !d) return "";
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function detectReportDateFromFileName(fileName = "", platform = "") {
  const name = String(fileName || "");
  const p = String(platform || "").toLowerCase();

  if (p === "uber") {
    // Example: 20260928-20260929-payments_driver-....csv
    const m = name.match(/(?:^|[^0-9])(20\d{2})(\d{2})(\d{2})-(20\d{2})(\d{2})(\d{2})(?:[^0-9]|$)/);
    if (m) return isoFromParts(m[1], m[2], m[3]);
  }

  if (p === "bolt") {
    // Example: Earnings per driver-5 Oct 2026-5 Oct 2026-....csv
    const months = {
      jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
      jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
    };
    const m = name.match(/-(\d{1,2})\s+([A-Za-z]{3})\s+(20\d{2})-/);
    if (m) {
      const month = months[String(m[2]).toLowerCase()];
      if (month) return isoFromParts(m[3], month, m[1]);
    }
  }

  return "";
}
